import { InboxItem } from "@cohesive/api-client";
import { format, isToday, isYesterday, subDays } from "date-fns";

export const GROUP_ORDER = [
  "Today",
  "Yesterday",
  "Previous 7 Days",
  "Previous 30 Days",
] as const;

export type GroupKey = (typeof GROUP_ORDER)[number] | string;

export function groupInboxItemsByDate<T extends InboxItem>(inboxItems: T[]) {
  const now = new Date();
  const sevenDaysAgo = subDays(now, 7);
  const thirtyDaysAgo = subDays(now, 30);

  return inboxItems
    .filter((inboxItem) => inboxItem.flow_session?.status !== "initialized")
    .reduce(
      (acc, inboxItem) => {
        const date = new Date(inboxItem.created_at);
        let key: GroupKey;

        if (isToday(date)) {
          key = "Today";
        } else if (isYesterday(date)) {
          key = "Yesterday";
        } else if (date > sevenDaysAgo) {
          key = "Previous 7 Days";
        } else if (date > thirtyDaysAgo) {
          key = "Previous 30 Days";
        } else {
          key = format(date, "MMMM yyyy");
        }

        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key]?.push(inboxItem);
        return acc;
      },
      {} as Record<GroupKey, T[]>,
    );
}

export function sortGroupedSessions<T>(
  entries: [GroupKey, T][],
): [GroupKey, T][] {
  return entries.sort(([a], [b]) => {
    if (GROUP_ORDER.includes(a as any) && GROUP_ORDER.includes(b as any)) {
      return GROUP_ORDER.indexOf(a as any) - GROUP_ORDER.indexOf(b as any);
    }
    if (GROUP_ORDER.includes(a as any)) return -1;
    if (GROUP_ORDER.includes(b as any)) return 1;
    return new Date(b).getTime() - new Date(a).getTime();
  });
}
